import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import {
  dimensionSchema,
  recommendationFeedbackReasonSchema,
  recommendationPrioritySchema,
  recommendationRatingSchema,
  recommendationStatusSchema,
  timeHorizonSchema,
} from './enums';
export const recommendationMetadataSchema = z
  .object({
    source: z.string().optional(),
    insightId: z.string().optional(),
    signalInstanceId: z.string().optional(),
  })
  .catchall(z.unknown())
  .nullable();
export type RecommendationMetadata = z.infer<typeof recommendationMetadataSchema>;

// An objective this bullet touches, never model-supplied.
// `name` is the objective's `title` in item-store, renamed here to match the field the frontend
// already renders.
//
// `matchStrength` is COVERAGE, not importance: the share of the bullet's distinct affected items
// that link to this objective. It deliberately says nothing about how much acting would move the
// objective -- impact magnitude is NEX-15701 and is not computed anywhere yet.
export const bulletObjectiveDTOSchema = z.object({
  id: z.number(),
  name: z.string(),
  matchStrength: z.number().optional(),
  derivedFrom: z.enum(['items', 'loop']).optional(),
});
export type BulletObjectiveDTO = z.infer<typeof bulletObjectiveDTOSchema>;

// One prioritised bullet inside a consolidated recommendation, as persisted in the row's `metadata`
// jsonb by apps/sir's persistConsolidatedRecommendation. Declared here so a reader can validate the
// blob instead of casting it. `catalogAction` and `actionType` stay plain strings on purpose: their
// unions live in apps/sir/src/computation-pass/, which this shared contract must not depend on.
export const persistedBulletDTOSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  actionType: z.string(),
  catalogAction: z.string(),
  timeHorizon: timeHorizonSchema,
  priority: recommendationPrioritySchema,
  // The model's own self-score for this bullet, a number here rather than the row's stringified
  // numeric column.
  confidence: z.number(),
  insightIds: z.array(z.string()),
  // Derived from the Loop attribution of the insights this bullet cites, never model-supplied, and
  // persisted as strings. Empty is the expected answer for a bullet about an org-scoped finding.
  loopIds: z.array(z.string()),
  affectedItemIds: z.array(z.number()).optional(),
  // Optional for two reasons: rows written before NEX-15651 do not have it, and an absent field is
  // meaningfully different from an empty one. Empty means resolution ran and nothing was linked;
  // absent means resolution did not run or failed. Both render as nothing on the surface, but only
  // the second is a signal worth chasing.
  objectives: z.array(bulletObjectiveDTOSchema).optional(),
  // The decision dimensions THIS bullet touches, derived from its own `catalogAction`.
  //
  // Optional because it is genuinely absent from the stored blob: like the recommendation-level
  // `dimensions`, it is resolved on the read path so a catalog edit needs no backfill. Responses
  // always carry it; a blob read straight from the database never does. Absent therefore means
  // "not resolved", never "touches nothing".
  //
  // The recommendation-level field is the union across bullets, which loses which bullet
  // contributed what — and a bullet IS a recommended action, so the surface needs it per bullet to
  // colour and tag each action line (NEX-16609 / NEX-16617).
  dimensions: z.array(dimensionSchema).optional(),
});
export type PersistedBulletDTO = z.infer<typeof persistedBulletDTOSchema>;

// The narrative's instructed length, stated in the generator's prompt and used by the card to decide
// when to clamp. Deliberately not a schema bound: rejecting an over-long narrative skips the org's pass.
export const NARRATIVE_BUDGET_CHARS = 240;

// The consolidated, org-scoped writer's whole metadata shape. Deliberately NOT an extension of
// recommendationMetadataSchema: the two writers share no metadata fields, and the literals on
// `source`/`scope` are what let a reader tell a consolidated row's blob from a legacy per-match one.
export const consolidatedRecommendationMetadataSchema = z.object({
  source: z.literal('consolidated'),
  scope: z.literal('org'),
  inputTokens: z.number(),
  outputTokens: z.number(),
  // Served most-severe-first: the read path bands this array by each bullet's derived `priority`,
  // then puts an objective-aligned bullet first inside a band, keeping the generating model's own
  // order among bullets that tie on both. The stored row is untouched, so a reader of the row and a
  // reader of the API do not see the same order.
  bullets: z.array(persistedBulletDTOSchema),
});
export type ConsolidatedRecommendationMetadataDTO = z.infer<
  typeof consolidatedRecommendationMetadataSchema
>;

// The measured confidence for one recommendation, rolled up across the dimensions that actually
// produced a value. `score` is weakest-link: the minimum across those dimensions, each already
// normalized to [0, 1]. `dimensions` carries the breakdown so a score is always explainable without
// a second call.
//
// `coverage` is how many of the dimensions expected at this layer produced a value at all, so a
// consumer can tell a well-scored recommendation measured on everything from one measured on a
// single dimension.
//
// THE DENOMINATOR IS NEVER 3. NEX-15315's confidence epic scoped four measures, but Relevance (the
// usefulness critic's sole output) was retired with that critic (NEX-17826) before any writer
// produced it, and User Feedback was never a pass-time dimension to begin with (see below) — so
// every writer's denominator is 2 today. The authoritative sets are EXPECTED_DIMENSIONS_BY_WRITER in
// apps/sir; read the denominator from there rather than counting this union.
//
// User Feedback is a human thumbs-up/down in `recommendation_feedback`, keyed per user and arriving
// long after the pass that wrote the row — it is not something a pass can measure, so it is not a
// dimension and never enters this figure. The epic's own Persistence note draws this line: "Data
// Quality, Groundedness, Relevance are traceable with the end recommendation… User Feedback persists
// separately, linked to the recommendation."
//
// Data Quality is here despite being an internal, signal-layer check: NEX-15318 propagates it
// weakest-link from the contributing signals precisely so a recommendation can say something about
// the quality of the data underneath it. It measures the inputs; Groundedness judges the output.
// min() across both is deliberate — a well-written recommendation resting on two data points should
// not read as high confidence.
//
// No current coverage figure is stated here on purpose: Groundedness is emitted by
// evidence-grounded-critic.ts's deriveMeasurements (NEX-15319), but not on every pass. What a row
// actually carries is `dimensions` below; EXPECTED_DIMENSIONS_BY_WRITER is what it is measured
// against, and it states no figure either.

// The dimension ids a recommendation can be scored on. Declared here rather than as a bare string
// key so a client can discriminate them (NEX-16439) — `z.record(z.string(), …)` typed the breakdown
// as an open bag and threw away a union the backend already had. This is the full set any writer
// could produce; which subset is expected of a given writer is EXPECTED_DIMENSIONS_BY_WRITER in
// apps/sir, and a new dimension is added in both.
export const recommendationConfidenceDimensions = ['data_quality', 'groundedness'] as const;
export const recommendationConfidenceDimensionSchema = z.enum(recommendationConfidenceDimensions);
export type RecommendationConfidenceDimensionDTO = z.infer<
  typeof recommendationConfidenceDimensionSchema
>;

export const measuredConfidenceDTOSchema = z.object({
  score: z.number(),
  coverage: z.number(),
  // Partial: only the dimensions that actually produced a value appear, which is what `coverage`
  // counts. An absent key means "not measured", never a zero.
  dimensions: z.partialRecord(recommendationConfidenceDimensionSchema, z.number()),
});
export type MeasuredConfidenceDTO = z.infer<typeof measuredConfidenceDTOSchema>;

// The calling user's own latest feedback per bullet, for seeding the card's already-voted state on
// load. `bulletId` is null for feedback rows written before the bullet_id column existed (record-
// level feedback, pre-NEX-16617) — a consumer treats that as "not attributable to a specific bullet"
// rather than dropping it.
export const viewerFeedbackDTOSchema = z.object({
  bulletId: z.string().nullable(),
  rating: recommendationRatingSchema.nullable(),
  reason: recommendationFeedbackReasonSchema.nullable(),
  comment: z.string().nullable(),
});
export type ViewerFeedbackDTO = z.infer<typeof viewerFeedbackDTOSchema>;

export const recommendationDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  title: z.string(),
  description: z.string(),
  actionType: z.string(),
  catalogAction: z.string().nullable(),
  timeHorizon: timeHorizonSchema.nullable(),
  priority: recommendationPrioritySchema,
  // The generating model's OWN self-assessment, passed through untouched. It is not a measurement
  // and nothing validates it — prefer `measuredConfidence` wherever both are present. Retained for
  // the debug view and for continuity; retiring it is NEX-16056.
  confidence: z.string().nullable(),
  status: recommendationStatusSchema,
  // Measured confidence. null when no dimension applied to this recommendation, which means "not
  // measured" and never an implicit pass or fail — a consumer must not render null as a low score.
  measuredConfidence: measuredConfidenceDTOSchema.nullable(),
  modelName: z.string().nullable(),
  // The decision dimensions this recommendation touches, derived on the read path from
  // `catalogAction` — there is no persisted column. Empty when the recommendation predates the
  // action catalog (`catalogAction: null`), which means "unknown", never "touches nothing": a
  // consumer must not read [] as a claim about the recommendation.
  dimensions: z.array(dimensionSchema),
  metadata: recommendationMetadataSchema,
  // The calling user's latest feedback, one entry per bullet. Resolved on the read path like
  // `dimensions` — never persisted on the row itself. Empty means "none from this user", never
  // "not loaded"; SYSTEM callers always get [] (see auth-context.ts's synthetic sys_ userId).
  viewerFeedback: z.array(viewerFeedbackDTOSchema).default([]),
  ...timestampDTOSchema.shape,
});
export type RecommendationDTO = z.infer<typeof recommendationDTOSchema>;

export const recommendationFeedbackDTOSchema = z.object({
  id: z.string(),
  recommendationId: z.string(),
  bulletId: z.string().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  rating: recommendationRatingSchema.nullable(),
  reason: recommendationFeedbackReasonSchema.nullable(),
  comment: z.string().nullable(),
  ...timestampDTOSchema.shape,
});
export type RecommendationFeedbackDTO = z.infer<typeof recommendationFeedbackDTOSchema>;
